import JSZip from 'jszip';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CallId, Group, GroupId, User, UserId, VideoCallEvent, VideoCallJpegEvent } from 'shared/models';
import { State } from 'reducers';
import { EcsTranscript } from 'logic/EcsTranscript';
import { useBuilding } from 'hooks/useBuilding/useBuilding';
import { getVideoCall } from 'selectors/ecsSelectors';
import { getUsers } from 'selectors/usersSelectors';
import { toError } from 'shared/util/toError';
import { useTranslation } from 'react-i18next';
import { callApi } from 'apiClient';
import { useEcsCallEvents } from 'hooks/useEcsCallEvents/useEcsCallEvents';

// Make the export take at least 2000 milliseconds
const MIN_EXPORT_TIME = 2000;

export function useVideoCallExport(callId: CallId) {
    const { t } = useTranslation();

    let percentComplete = 0;

    const startTimeRef = useRef(0);
    
    const [ downloading, setDownloading ] = useState(false);
    const [ exportPercent, setExportPercent ] = useState(0);

    const exportDataUrlRef = useRef<string>();
    const [ exportErrorMessage, setExportErrorMessage ] = useState('');


    const videoCall = useSelector((state: State) => getVideoCall(state, { callId }));
    const buildingId = videoCall ? videoCall.buildingId : null;
    const { building } = useBuilding(buildingId);

    const users: Record<UserId, User> = useSelector(getUsers);

    const { errKey, loadEvents } = useEcsCallEvents(callId);

    // Set the error message when the errKey from useEcsCallEvents changes
    useEffect(() => {
        setExportErrorMessage(errKey && t(errKey));
    }, [ errKey, setExportErrorMessage, t ]);

    const ecsTranscriptRef = useRef<EcsTranscript>();

    const callback = useCallback(async (events: VideoCallEvent[]) => {
        let groups: Record<GroupId, Group>;
        try {
            groups = await callApi('GET', `/api/buildings/${buildingId}/device-groups`);
        }
        catch (e) {
            const err = toError(e);
            setExportErrorMessage(t(err.message));
            return;
        }

        const elevator = groups[videoCall!.elevatorId];

        ecsTranscriptRef.current = new EcsTranscript(videoCall!, events, building, elevator, users, t);

        // Once downloading has completed, decide how much progress bar to advance.
        // Do at most 25% if the download took at least five seconds.
        percentComplete = Math.round(Math.min(5000, performance.now() - startTimeRef.current) / 5000 * 25);
        setExportPercent(percentComplete);

        try {
            const zip = new JSZip();
            zip.file(`${callId}-transcript.csv`, ecsTranscriptRef.current.toCsv());
    
            const frames =
                (events as VideoCallJpegEvent[])
                    .filter(e => e.type === 'JPEG')
                    .map(e => ({
                        jpeg: e.details.jpeg && Uint8Array.from(atob(e.details.jpeg), x => x.charCodeAt(0)),
                        timestamp: e.timestamp,
                    }));
    
            percentComplete += 10;
            setExportPercent(percentComplete);
        
            if (frames.length > 0) {
                const percentIncrementPerFrame = (95 - percentComplete) / frames.length;
                let accumulatedIncrement = 0;
                    
                // TODO: would be nice to show the timestamps in the building's timezone
    
                // Add each of the JPEG frames to the zip file
                for (const frame of frames) {
                    if (frame.jpeg) {
                        const filename = `frames/${moment.utc(frame.timestamp).format('YYYY-MM-DD_HH-mm-ss-SSS')}.jpg`;
                        zip.file(filename, frame.jpeg);
                    }
    
                    // Announce progress... but only when it will make a noticeable difference (i.e. at least 5%)
                    accumulatedIncrement += percentIncrementPerFrame;
                    if (accumulatedIncrement > 5) {
                        percentComplete += accumulatedIncrement;
                        accumulatedIncrement = 0;
                        setExportPercent(percentComplete);
                        await new Promise(resolve => setTimeout(resolve, 15));
                    }
                }
            }
    
            const blob = await zip.generateAsync({ type: 'blob' });
    
            // Slow down the operation if it happened faster than two seconds
            // (because we don't want the animation to flash too quickly)
            const done = performance.now();
            const duration = done - startTimeRef.current;
            if (duration < MIN_EXPORT_TIME) {
                await new Promise(resolve => setTimeout(resolve, MIN_EXPORT_TIME - duration));
            }
        
            setExportPercent(100);
            
            exportDataUrlRef.current = window.URL.createObjectURL(blob);
        }
        catch (e) {
            const err = toError(e);
            setExportErrorMessage(t([ `ERROR.${err.message}`, 'SERVER_ERROR' ]));
        }
    }, [ buildingId, videoCall, startTimeRef, exportDataUrlRef, ecsTranscriptRef, setExportPercent, setDownloading, setExportErrorMessage ]);

    const beginExport = useCallback(() => {
        // Bail out if we're already in the process of exporting
        if (startTimeRef.current) {
            return;
        }
        // Bail out if the building hasn't loaded yet
        if (!building) {
            return;
        }

        setExportPercent(0);
        setExportErrorMessage('');

        // Determine when we start so we can force the operation to take two seconds
        // (so the animation doesn't flash quickly and look jarring to the user)
        startTimeRef.current = performance.now();

        // Request any remaining events for the call
        setDownloading(true);
        void (async () => {
            let events: VideoCallEvent[] | null = null;
            try {
                events = await loadEvents();
            }
            finally {
                setDownloading(false);
            }

            if (events === null) return;
            await callback(events);
        })();
    }, [ building, callback, startTimeRef, loadEvents, setExportPercent, setDownloading, setExportErrorMessage ]);

    const clearExport = useCallback(() => {
        setExportPercent(0);
        setExportErrorMessage('');
        setDownloading(false);

        const url = exportDataUrlRef.current;
        if (url) {
            window.URL.revokeObjectURL(url);
        }
        exportDataUrlRef.current = '';
    }, [ setExportPercent, setExportErrorMessage, setDownloading, exportDataUrlRef ]);

    //
    // Clean up the memory resource when we're unmounted
    //
    useEffect(() => {
        return () => {
            const url = exportDataUrlRef.current;
            if (url) {
                window.URL.revokeObjectURL(url);
            }
        };
    }, [ exportDataUrlRef ]);


    return {
        beginExport,
        clearExport,
        downloading,
        exportUrl: exportDataUrlRef.current,
        exportPercent,
        exportErrorMessage,
    };
}
